import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { PosEntry, PosStatus } from './types';
import { Badge } from '../../components/Badge';
import { Drawer } from '../../components/Drawer';
import { DEMO_ACTION_LISTS } from './demoData';
import { WriteEmailModal } from './CrmModals';
import { UploadWinelistModal, PosDocument } from './PosActionModals';

const statusToVariant = (s: PosStatus) =>
  s === 'prospect' ? ('default' as const) : s;

interface PosDrawerProps {
  pos: PosEntry | null;
  open: boolean;
  onClose: () => void;
}

export const PosDrawer: React.FC<PosDrawerProps> = ({ pos, open, onClose }) => {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  // Per-POS demo state (mirrors the prototype's in-memory DOCS_BY_POS map —
  // no dedicated API yet).
  const [docsByPos, setDocsByPos] = useState<Record<number, PosDocument[]>>({});

  // Prototype's drawer always closes its own modals when a different POS
  // is opened.
  useEffect(() => {
    setUploadModalOpen(false);
    setEmailModalOpen(false);
  }, [pos?.id]);

  if (!pos)
    return (
      <Drawer open={false} onClose={onClose}>
        <span />
      </Drawer>
    );

  const eyebrow = `POS · ${pos.type.toUpperCase()} · ${pos.city.toUpperCase()}, ${pos.country.toUpperCase()}`;

  const containingLists = DEMO_ACTION_LISTS.filter(
    (l) =>
      pos.actionListIds?.includes(l.id) || l.pos.some((p) => p.id === pos.id)
  );

  const documents = docsByPos[pos.id] ?? [];

  const handleUploadWinelist = (doc: PosDocument) => {
    setDocsByPos((prev) => ({
      ...prev,
      [pos.id]: [doc, ...(prev[pos.id] ?? [])]
    }));
  };

  const handleSendEmail = (data: { subject: string; message: string }) => {
    void data;
  };

  return (
    <>
      <Drawer open={open} onClose={onClose} title={pos.name} eyebrow={eyebrow}>
        <Content>
          <SubLine>
            {pos.region} · {pos.country} · {pos.competitors} competitors tracked
          </SubLine>

          <BadgeRow>
            <Badge variant={statusToVariant(pos.status)}>
              {pos.status === 'towin'
                ? 'To Win'
                : pos.status.charAt(0).toUpperCase() + pos.status.slice(1)}
            </Badge>
            <Badge variant="default">
              {pos.network === 'on' ? 'ON-TRADE' : 'OFF-TRADE'}
            </Badge>
            {pos.stability && (
              <Badge variant="default">
                Stability ·{' '}
                {pos.stability.charAt(0).toUpperCase() + pos.stability.slice(1)}
              </Badge>
            )}
            {pos.stage && (
              <Badge variant="default">
                Stage · {pos.stage.charAt(0).toUpperCase() + pos.stage.slice(1)}
              </Badge>
            )}
          </BadgeRow>

          <Actions>
            <ActionBtn type="button" onClick={() => setEmailModalOpen(true)}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
                <polyline points="22 6 12 13 2 6" />
              </svg>
              Email
            </ActionBtn>
            <ActionBtn type="button" onClick={() => setUploadModalOpen(true)}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              Upload winelist
            </ActionBtn>
          </Actions>

          {/* ─── ACTIVITY ─── */}
          <Section>
            <SectionTitleRow>
              <SectionTitle>ACTIVITY</SectionTitle>
              <AddActivityBtn>+ Add activity</AddActivityBtn>
            </SectionTitleRow>
            {pos.activity && pos.activity.length > 0 ? (
              <ActivityTimeline>
                {pos.activity.map((act) => (
                  <ActivityRow key={act.id}>
                    <ActivityIcon>
                      <ActivityIconCircle />
                    </ActivityIcon>
                    <ActivityContent>
                      <ActivityTitle>{act.title}</ActivityTitle>
                      <ActivityMeta>
                        {act.when} · by {act.user}
                      </ActivityMeta>
                    </ActivityContent>
                  </ActivityRow>
                ))}
              </ActivityTimeline>
            ) : (
              <EmptyText>No activity recorded yet.</EmptyText>
            )}
          </Section>

          {/* ─── WINE LIST CHANGE HISTORY ─── */}
          {pos.history && pos.history.length > 0 && (
            <Section>
              <SectionTitle>WINE LIST CHANGE HISTORY</SectionTitle>
              <TableWrap>
                <HistTable>
                  <thead>
                    <tr>
                      <th>DATE</th>
                      <th>METHOD</th>
                      <th>CLIENT STATUS</th>
                      <th className="num">CLIENT REFS</th>
                      <th className="num">TOTAL REFS</th>
                      <th>NOTES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pos.history.map((h, i) => (
                      <tr key={i}>
                        <td>{h.date}</td>
                        <td>
                          <MethodBadge>{h.method.toUpperCase()}</MethodBadge>
                        </td>
                        <td>
                          <Badge variant={statusToVariant(h.clientStatus)}>
                            {h.clientStatus}
                          </Badge>
                        </td>
                        <td className="num">{h.clientRefs}</td>
                        <td className="num">{h.totalRefs}</td>
                        <td className="notes">{h.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </HistTable>
              </TableWrap>
            </Section>
          )}

          {/* ─── KEY METRICS ─── */}
          <Section>
            <SectionTitle>KEY METRICS</SectionTitle>
            <MetricsGrid>
              <Metric>
                <MetricLabel>Your refs</MetricLabel>
                <MetricValue>{pos.yourRef}</MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>Total refs</MetricLabel>
                <MetricValue>{pos.totalRefs}</MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>Competitors</MetricLabel>
                <MetricValue>{pos.competitors}</MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>Evolution</MetricLabel>
                <MetricValue
                  $positive={pos.evolution > 0}
                  $negative={pos.evolution < 0}
                >
                  {pos.evolution > 0 ? '+' : ''}
                  {pos.evolution}
                </MetricValue>
              </Metric>
            </MetricsGrid>
          </Section>

          {/* ─── CONTACT INFORMATION ─── */}
          <Section>
            <SectionTitle>CONTACT INFORMATION</SectionTitle>
            <InfoGrid>
              {pos.phone && (
                <InfoRow>
                  <InfoLabel>Phone</InfoLabel>
                  <InfoValue>{pos.phone}</InfoValue>
                </InfoRow>
              )}
              {pos.email && (
                <InfoRow>
                  <InfoLabel>Email</InfoLabel>
                  <InfoValue>
                    <a href={`mailto:${pos.email}`}>{pos.email}</a>
                  </InfoValue>
                </InfoRow>
              )}
              {pos.website && (
                <InfoRow>
                  <InfoLabel>Website</InfoLabel>
                  <InfoValue>
                    <a
                      href={`https://${pos.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {pos.website}
                    </a>
                  </InfoValue>
                </InfoRow>
              )}
              {pos.address && (
                <InfoRow>
                  <InfoLabel>Address</InfoLabel>
                  <InfoValue>{pos.address}</InfoValue>
                </InfoRow>
              )}
              {pos.primaryContact && (
                <InfoRow>
                  <InfoLabel>Primary contact</InfoLabel>
                  <InfoValue>
                    {pos.primaryContact}{' '}
                    {pos.primaryContactRole && (
                      <InfoSub>· {pos.primaryContactRole}</InfoSub>
                    )}
                  </InfoValue>
                </InfoRow>
              )}
              {pos.wineListSize != null && (
                <InfoRow>
                  <InfoLabel>Wine list size</InfoLabel>
                  <InfoValue>{pos.wineListSize} references</InfoValue>
                </InfoRow>
              )}
              {pos.avgPrice && (
                <InfoRow>
                  <InfoLabel>Avg. price</InfoLabel>
                  <InfoValue>{pos.avgPrice}</InfoValue>
                </InfoRow>
              )}
              {pos.lastChecked && (
                <InfoRow>
                  <InfoLabel>Last checked</InfoLabel>
                  <InfoValue>{pos.lastChecked}</InfoValue>
                </InfoRow>
              )}
            </InfoGrid>
          </Section>

          {/* ─── MY REFERENCES ─── */}
          {pos.myRefs && pos.myRefs.length > 0 && (
            <Section>
              <SectionTitle>
                MY REFERENCES{' '}
                <SectionSub>
                  CURRENT LISTINGS · {pos.shelves ?? pos.myRefs.length} wine
                  {(pos.shelves ?? pos.myRefs.length) !== 1 ? 's' : ''}
                </SectionSub>
              </SectionTitle>
              <TableWrap>
                <RefsTable>
                  <thead>
                    <tr>
                      <th>Wine</th>
                      <th>Vintage</th>
                      <th>Format</th>
                      <th className="num">Price</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pos.myRefs.map((r, i) => (
                      <tr key={i}>
                        <td>{r.wine}</td>
                        <td>{r.vintage}</td>
                        <td>{r.format}</td>
                        <td className="num">{r.price}</td>
                        <td>
                          <RefStatusBadge $status={r.status}>
                            {r.status}
                          </RefStatusBadge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </RefsTable>
              </TableWrap>
            </Section>
          )}

          {/* ─── COMPETITOR REFERENCES ─── */}
          {pos.competitorRefs && pos.competitorRefs.length > 0 && (
            <Section>
              <SectionTitle>
                COMPETITOR REFERENCES{' '}
                <SectionSub>
                  COMPETITOR WINES · {pos.competitors} tracked
                </SectionSub>
              </SectionTitle>
              <TableWrap>
                <RefsTable>
                  <thead>
                    <tr>
                      <th>Wine</th>
                      <th>Vintage</th>
                      <th>Format</th>
                      <th className="num">Price</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pos.competitorRefs.map((r, i) => (
                      <tr key={i}>
                        <td>{r.wine}</td>
                        <td>{r.vintage}</td>
                        <td>{r.format}</td>
                        <td className="num">{r.price}</td>
                        <td>
                          <RefStatusBadge $status={r.status}>
                            {r.status}
                          </RefStatusBadge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </RefsTable>
              </TableWrap>
            </Section>
          )}

          {/* ─── COLLECTED DOCUMENTS ─── */}
          <Section>
            <SectionTitle>
              COLLECTED WINE LISTS
              {documents.length > 0 && (
                <SectionSub>{documents.length}</SectionSub>
              )}
            </SectionTitle>
            {documents.length > 0 ? (
              <DocList>
                {documents.map((doc) => (
                  <DocRow key={doc.id}>
                    <DocIcon>
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="9" y1="13" x2="15" y2="13" />
                        <line x1="9" y1="17" x2="15" y2="17" />
                      </svg>
                    </DocIcon>
                    <DocMeta>
                      <DocName>{doc.name}</DocName>
                      <DocSub>
                        {doc.type} · Collected {doc.date}
                      </DocSub>
                    </DocMeta>
                  </DocRow>
                ))}
              </DocList>
            ) : (
              <EmptyText>No documents collected for this POS yet.</EmptyText>
            )}
          </Section>

          {/* ─── IN ACTION LISTS ─── */}
          <Section>
            <SectionTitle>IN ACTION LISTS</SectionTitle>
            {containingLists.length > 0 ? (
              <ListChips>
                {containingLists.map((l) => (
                  <ListChip key={l.id}>
                    <ListChipDot $type={l.sentToType} />
                    {l.name}
                  </ListChip>
                ))}
              </ListChips>
            ) : (
              <EmptyText>Not in any action list yet.</EmptyText>
            )}
          </Section>
        </Content>
      </Drawer>

      {/* Rendered outside the sliding `Drawer` panel: the panel has a CSS
          `transform`, which would otherwise turn it into the containing
          block for these modals' `position: fixed` overlays and confine
          them to the drawer's box instead of the full viewport. */}
      <UploadWinelistModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUpload={handleUploadWinelist}
      />
      <WriteEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        selectedPos={[pos]}
        onSend={handleSendEmail}
      />
    </>
  );
};

/* ─── Styled Components ─── */

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SubLine = styled.p`
  font-size: 13px;
  color: var(--ink-muted);
  margin: 0;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 0;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
`;

const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 13px;
  border: 1px solid var(--line);
  border-radius: 7px;
  background: var(--white);
  color: var(--ink);
  font-size: 13px;
  cursor: pointer;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--ink);
  }

  &.primary {
    background: var(--ink);
    color: var(--white);
    border-color: var(--ink);
  }

  &.primary:hover {
    background: var(--terracotta);
    border-color: var(--terracotta);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SectionTitle = styled.h4`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-faint);
  font-weight: 600;
  margin: 0;
`;

const SectionSub = styled.span`
  font-size: 10px;
  color: var(--ink-faint);
  margin-left: 8px;
  font-weight: 400;
`;

const AddActivityBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: 1px solid var(--terracotta);
  border-radius: 6px;
  background: transparent;
  color: var(--terracotta);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: var(--terracotta);
    color: var(--white);
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const Metric = styled.div`
  background: var(--paper, #f8f7f5);
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetricLabel = styled.span`
  font-size: 10px;
  color: var(--ink-faint);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
`;

const MetricValue = styled.span<{ $positive?: boolean; $negative?: boolean }>`
  font-family: var(--font-serif, 'Fraunces', serif);
  font-size: 22px;
  font-weight: 500;
  color: ${({ $positive, $negative }) =>
    $positive ? 'var(--olive)' : $negative ? 'var(--crimson)' : 'var(--ink)'};
`;

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 12px;
  font-size: 13px;
`;

const InfoLabel = styled.span`
  min-width: 110px;
  color: var(--ink-faint);
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: var(--ink);

  a {
    color: var(--terracotta);
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const InfoSub = styled.span`
  color: var(--ink-faint);
  font-size: 12px;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid var(--line);
`;

const RefsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  thead {
    background: var(--paper, #f8f7f5);
  }

  th {
    padding: 8px 12px;
    text-align: left;
    font-weight: 600;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-faint);
    border-bottom: 1px solid var(--line);
  }

  td {
    padding: 8px 12px;
    border-bottom: 1px solid var(--line);
    color: var(--ink);
  }

  tr:last-child td {
    border-bottom: none;
  }

  .num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`;

const HistTable = styled(RefsTable)`
  .notes {
    font-style: italic;
    color: var(--ink-muted);
    font-size: 11px;
  }
`;

const RefStatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${({ $status }) =>
    $status === 'listed'
      ? 'var(--olive-light, #e8f5e0)'
      : $status === 'new'
        ? 'var(--blue-light, #e0f0ff)'
        : 'var(--crimson-light, #ffe6e6)'};
  color: ${({ $status }) =>
    $status === 'listed'
      ? 'var(--olive, #4a7c3f)'
      : $status === 'new'
        ? 'var(--blue, #2563eb)'
        : 'var(--crimson, #c0392b)'};
`;

const MethodBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  background: var(--paper, #f8f7f5);
  color: var(--ink-muted);
  letter-spacing: 0.04em;
`;

const ActivityTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
  padding-left: 20px;

  &::before {
    content: '';
    position: absolute;
    left: 9px;
    top: 6px;
    bottom: 6px;
    width: 2px;
    background: var(--terracotta, #c85a3a);
    border-radius: 1px;
    opacity: 0.3;
  }
`;

const ActivityRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 10px 0;
  position: relative;
`;

const ActivityIcon = styled.div`
  position: absolute;
  left: -20px;
  top: 14px;
`;

const ActivityIconCircle = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--paper-warm, #f5f0eb);
  border: 2px solid var(--terracotta, #c85a3a);
  display: grid;
  place-items: center;
`;

const ActivityContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ActivityTitle = styled.span`
  font-size: 13px;
  color: var(--ink);
  font-weight: 500;
`;

const ActivityMeta = styled.span`
  font-size: 11px;
  color: var(--ink-faint);
`;

const DocList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DocRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--paper, #f8f7f5);
`;

const DocIcon = styled.span`
  flex-shrink: 0;
  color: var(--terracotta, #c85a3a);

  svg {
    width: 18px;
    height: 18px;
  }
`;

const DocMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const DocName = styled.span`
  font-size: 13px;
  color: var(--ink);
  font-weight: 500;
`;

const DocSub = styled.span`
  font-size: 11px;
  color: var(--ink-faint);
`;

const ListChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ListChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: var(--white);
  font-size: 12px;
  color: var(--ink);
  cursor: pointer;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--ink);
  }
`;

const ListChipDot = styled.span<{ $type: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $type }) =>
    $type === 'distributor'
      ? 'var(--terracotta, #c85a3a)'
      : 'var(--olive, #4a7c3f)'};
`;

const EmptyText = styled.p`
  font-size: 13px;
  color: var(--ink-faint);
  margin: 0;
  font-style: italic;
`;
